import React, { useState } from 'react';
import {
  IAuditoriaIntegridadeLancamentosResultado,
  IItemIntegridadeLancamentos,
} from '../interfaces/IAuditoriaIntegridadeLancamentos';
import { formatCurrency, formatDate } from '../utils/formatters';

interface Props {
  resultado: IAuditoriaIntegridadeLancamentosResultado;
}



const getIconeStatus = (status: string) => {
  switch (status) {
    case 'ERRO':
      return 'bi-exclamation-circle-fill';
    case 'ALERTA':
      return 'bi-exclamation-triangle-fill';
    case 'AVISO':
      return 'bi-info-circle-fill';
    default:
      return 'bi-check-circle-fill';
  }
};

const AuditoriaIntegridadeLancamentosDetalhe: React.FC<Props> = ({ resultado }) => {
  const [itemExpandido, setItemExpandido] = useState<string | null>(null);
  const [fundamentacaoAberta, setFundamentacaoAberta] = useState(false);
  const [apenasFechamento, setApenasFechamento] = useState(false);

  const lancamentosGenericos = resultado.lancamentosGenericos || [];
  const lancamentosExibidos = apenasFechamento
    ? lancamentosGenericos.filter((item) => item.ajusteFechamento)
    : lancamentosGenericos;

  const alternarExpandir = (chave: string) => {
    setItemExpandido(itemExpandido === chave ? null : chave);
  };

  return (
    <>
      <div className={`auditoria-alert status-${resultado.status}`}>
        <i className={`bi ${getIconeStatus(resultado.status)} auditoria-alert-icon`} />
        <div>
          <strong>Auditoria de Integridade e Rastreabilidade de Lançamentos — {resultado.status}</strong>
          <div>{resultado.mensagemDirecionamento}</div>
        </div>
      </div>

      <div className='auditoria-kpis'>
        <div className={`auditoria-kpi ${resultado.impactoFinanceiroTotal > 0 ? 'auditoria-kpi-critico' : 'auditoria-kpi-disabled'}`}>
          <div className='auditoria-kpi-label'>Volume sob Risco Documental</div>
          <div className='auditoria-kpi-value'>{formatCurrency(resultado.impactoFinanceiroTotal)}</div>
          {resultado.impactoFinanceiroTotal === 0 ? (
            <span className='auditoria-kpi-selo'>Zero Desperdício</span>
          ) : (
            <div className='auditoria-kpi-help'>Multa de 5% contingente, limitada a 1% da receita bruta do período (Art. 12, II, Lei 8.218/1991), se não sanado na ECD.</div>
          )}
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Lançamentos Analisados / Genéricos</div>
          <div className='auditoria-kpi-value'>
            {resultado.totalLancamentosAnalisados} / <span className='auditoria-contador auditoria-contador-alerta'>{lancamentosGenericos.length}</span>
          </div>
          <div className='auditoria-kpi-help'>Lançamentos manuais analisados no período (exclui saldos/encerramento automáticos).</div>
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Fundamentação</div>
          <div className='auditoria-kpi-value'>
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              onClick={() => setFundamentacaoAberta(!fundamentacaoAberta)}
            >
              <i className='bi bi-journal-check me-1' />
              {fundamentacaoAberta ? 'Ocultar fundamentação' : 'Ver fundamentação legal'}
            </button>
          </div>
          <div className='auditoria-kpi-help'>Base legal aplicada à auditoria documental.</div>
        </div>
      </div>

      {fundamentacaoAberta && (
        <div className='auditoria-card'>
          <div><strong>Integridade de Lançamentos:</strong> {resultado.fundamentacaoIntegridade}</div>
        </div>
      )}

      <div className='auditoria-card'>
        <div className='d-flex align-items-center justify-content-between flex-wrap gap-2'>
          <h3 className='mb-0'>Monitor de Integridade de Lançamentos (Ajustes)</h3>
          <div className='form-check form-switch'>
            <input
              className='form-check-input'
              type='checkbox'
              id='filtro-ajuste-fechamento'
              checked={apenasFechamento}
              onChange={(e) => setApenasFechamento(e.target.checked)}
            />
            <label className='form-check-label' htmlFor='filtro-ajuste-fechamento' style={{ fontSize: 13 }}>
              Mostrar só ajustes de fechamento (último dia do mês/exercício)
            </label>
          </div>
        </div>
        <p className='text-muted'>
          Lançamentos manuais (Ativo, Passivo e Resultado) com histórico genérico e ausência de identificação
          (CPF/CNPJ, nome ou palavra estrutural conhecida) — fragilidade documental (ITG 2000 / Art. 290 do
          RIR/2018). A persistência destes dados na ECD sujeita a empresa a multa de 5% sobre o montante, limitada
          a 1% da receita bruta do período (Art. 12, II, Lei 8.218/1991).
        </p>

        {lancamentosExibidos.length === 0 && (
          <div className='text-muted mt-4'>Nenhum lançamento com histórico genérico identificado no período.</div>
        )}

        {lancamentosExibidos.length > 0 && (
          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Conta</th>
                  <th>Data</th>
                  <th>Histórico</th>
                  <th className='text-end'>Valor</th>
                  <th className='text-center'>Ajuste Fechamento</th>
                  <th className='text-end'>Multa Potencial (5%, lim. 1% RB)</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {lancamentosExibidos.map((item: IItemIntegridadeLancamentos, idx) => {
                  const chave = `lg-${item.empresaId}-${item.contaContabil}-${idx}`;
                  const expandido = itemExpandido === chave;

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo status-alerta' onClick={() => alternarExpandir(chave)}>
                        <td>
                          <strong>{item.descricaoConta || item.contaContabil}</strong>
                          <div className='text-muted' style={{ fontSize: 11 }}>{item.contaContabil}</div>
                        </td>
                        <td>{formatDate(item.dataLancamento)}</td>
                        <td style={{ maxWidth: 280 }}>{item.historico}</td>
                        <td className='text-end'>{formatCurrency(item.valor)}</td>
                        <td className='text-center'>
                          {item.ajusteFechamento && <span className='auditoria-status status-ALERTA'>Sim</span>}
                          {!item.ajusteFechamento && '-'}
                        </td>
                        <td className='text-end'>{formatCurrency(item.multaPotencial)}</td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={7} style={{ background: '#fffbeb' }}>
                            <div className='py-2 px-3'>
                              <div><strong>Diagnóstico:</strong> {item.mensagemDiagnostico}</div>
                              <div><strong>Memória de Cálculo:</strong> {item.memoriaCalculo}</div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default AuditoriaIntegridadeLancamentosDetalhe;
